<template>
  <div
    class="relative w-full h-full dark:bg-dark-box-background"
    :class="[fetched_all_notes && new_notes.length == 0 ? '' : 'overflow-y-auto']">
    <div class="flex justify-between items-center">
      <div class="flex items-center truncate overflow-ellipsis">
        <div
          v-if="is_add_new_note || is_edit_note"
          class="mr-1"
          @click="
            () => {
              is_add_new_note = false
              is_edit_note = false
              note = { title: null, description: null, categories: [] }
            }
          ">
          <ChevronLeft class="cursor-pointer h-5 w-5" aria-hidden="true" />
        </div>
        <span class="mr-1 text-[20px] not-italic font-medium font-sans truncate overflow-ellipsis">{{
          $utils.extractValue(label, "N/A", $route)
        }}</span>
      </div>
      <button type="button" class="primary-btn w-fit" @click="createUpdateNote()">
        <span class="text-[14px] not-italic font-normal font-sans leading-5">{{
          is_add_new_note || is_edit_note ? $t("Save") : $t("Add new note")
        }}</span>
      </button>
    </div>

    <div v-if="!is_add_new_note && !is_edit_note" class="h-[calc(100%-48px)]">
      <div v-if="!fetched_all_notes && !is_add_new_note && !is_edit_note" class="text-common-grey">
        {{ $t("Loading...") }}
      </div>
      <div
        v-if="fetched_all_notes && new_notes.length == 0 && !is_add_new_note && !is_edit_note"
        class="w-full h-full -mt-6 flex items-center justify-center text-[14px] not-italic font-normal font-sans leading-5">
        {{ $t("You have no notes added") }}
      </div>
      <div
        v-if="fetched_all_notes && !is_add_new_note && !is_edit_note"
        class="relative w-full h-[calcl(100%-66px)] mb-2 mt-2"
        :class="[{ 'pointer-events-none': loading }]">
        <div v-if="new_notes.length > 0" class="flex flex-col w-32">
          <div
            class="flex justify-between items-center h-8 max-h-8 dark:bg-dark-screen-background rounded-full cursor-pointer group px-4 py-2 shadow-lg border-2"
            :style="{
              background: `${selected_filters.length == 0 || selected_filters.length >= 2 ? '#202B31' : `${selected_filters[0].color}`}`
            }"
            @click="
              () => {
                filter_is_open = !filter_is_open
              }
            ">
            <div class="truncate font-bold text-white dark:text-dark-body-text text-sm capitalize">
              {{
                selected_filters.length > 1 ? $t("See filter") : selected_filters.length == 1 ? selected_filters[0].name : $t("Show all")
              }}
            </div>
            <ChevronDown class="w-5 h-5 text-white" aria-hidden="true" />
          </div>
          <span class="text-[13px] text-common-grey">{{
            selected_filters.length == 0
              ? $t("Showing all") + ` ${new_notes.length} ` + $t("notes")
              : $t("Showing") +
                ` ${new_notes.filter((n) => n.categories?.some((r) => selected_filters.map((f) => f.id).indexOf(r) >= 0)).length} ` +
                $t("of") +
                ` ${new_notes.length} ` +
                $t("notes")
          }}</span>
          <div v-if="filter_is_open" class="absolute bg-white top-9 z-10 w-32">
            <div
              class="font-bold text-white dark:text-dark-body-text dark:bg-dark-screen-background rounded-full cursor-pointer group flex items-center px-4 mb-1 text-sm justify-center capitalize shadow-lg border-2"
              :style="{
                background: `${selected_filters.length == 0 ? '#202B31' : '#FFFFFF'}`,
                color: `${selected_filters.length == 0 ? '#FFFFFF' : '#000000'}`,
                borderColor: '#000000'
              }"
              @click.stop="
                () => {
                  filter_is_open = false
                  selected_filters = []
                }
              ">
              {{ $t("All") }}
            </div>
            <div
              v-for="filter in all_filters"
              :key="filter.id"
              class="font-bold text-white dark:text-dark-body-text dark:bg-dark-screen-background rounded-full cursor-pointer group flex items-center px-4 mb-1 text-sm justify-center capitalize shadow-lg border-2"
              :style="{
                background: `${selected_filters.find((f) => f.id == filter.id) ? filter.color : '#FFFFFF'}`,
                color: `${selected_filters.find((f) => f.id == filter.id) ? '#FFFFFF' : filter.color}`,
                borderColor: `${filter.color}`
              }"
              @click.stop="handleFilter(filter)">
              {{ filter.name }}
            </div>
          </div>
        </div>
        <div
          v-for="note in selected_filters.length == 0
            ? new_notes
            : new_notes.filter((n) => n.categories?.some((r) => selected_filters.map((f) => f.id).indexOf(r) >= 0))"
          :key="note.id"
          class="relative flex flex-col border rounded-lg mb-2">
          <div
            v-show="fetched_categories"
            class="absolute right-0 h-full w-2 rounded-r-lg"
            :style="{ background: `${initial_categories.find((cat) => note.categories?.includes(cat.id))?.color}` }" />

          <div class="pl-2 pr-4 py-2">
            <div class="flex justify-between">
              <div v-if="tied_company" class="has-tooltip">
                <img
                  v-if="$utils.getDomainName(tied_company?.values[$utils.getOptionMetricReference('company', 'website')])"
                  class="inline-block object-contain w-6 rounded-lg"
                  :src="`https://logosnap.io/api/${$utils.getDomainName(
                    tied_company?.values[$utils.getOptionMetricReference('company', 'website')]
                  )}?token=ecoTB81m5dQqkrjw`"
                  :alt="``">

                <Avatar
                  v-else
                  :hover-effect="true"
                  :first-name="
                    $utils.getRecordName(tied_company) ? $utils.getRecordName(tied_company) : Record.from(tied_company).info?.name
                  "
                  :last-name="null" />

                <span class="flex-wrap px-1 text-sm text-white bg-gray-700 rounded tooltip whitespace-nowrap">{{
                  tied_company?.name
                }}</span>
              </div>
              <div v-show="fetched_categories" class="flex items-center gap-1 overflow-x-scroll">
                <div
                  v-for="category in note?.is_all_assigned_categories_shown ? note.categories : note.categories.slice(0, 3)"
                  :key="category"
                  class="flex items-center justify-center rounded-lg px-1"
                  :style="{ background: `${initial_categories.find((cat) => category.includes(cat.id))?.color}` }">
                  <span class="truncate text-[12px] font-bold text-white">{{
                    initial_categories.find((cat) => category.includes(cat.id))?.name
                  }}</span>
                </div>
                <div
                  v-if="note.categories.length > 3 && !note?.is_all_assigned_categories_shown"
                  class="flex items-center justify-center rounded-lg px-1 bg-[#202B31] cursor-pointer"
                  @click="note['is_all_assigned_categories_shown'] = true">
                  <span class="text-[12px] font-bold text-white">+{{ note.categories.length - 3 }} {{ $t("more") }}</span>
                </div>
                <div
                  v-if="note?.is_all_assigned_categories_shown"
                  class="flex items-center justify-center rounded-lg px-1 bg-[#202B31] cursor-pointer"
                  @click="note['is_all_assigned_categories_shown'] = false">
                  <span class="text-[12px] font-bold text-white flex whitespace-nowrap">{{ $t("Show less") }}</span>
                </div>
              </div>
            </div>
            <div
              class="h-[20px] max-h-[20px] min-h-[20px] truncate overflow-ellipsis text-[14px] not-italic font-medium font-sans leading-normal">
              {{ note.title }}
            </div>
            <TextEditor
              :combined-values-listner="label + note.description"
              class="-my-2 h-[38px] max-h-[38px] min-h-[38px] truncate overflow-ellipsis relative"
              input-classes="-ml-4"
              :transparent-bg="true"
              :disabled="true"
              :no-border="true"
              :value="$utils.extractValue(note.description, 'N/A', $route)"
              :name="`description`"
              label="" />
            <div class="flex justify-between items-center">
              <div class="flex items-center">
                <div class="bg-white rounded-l-lg cursor-pointer" @click="editNote(note)">
                  <FilePenLine class="relative w-5 h-5 opacity-50 hover:opacity-100" />
                </div>
                <Trash2
                  v-if="!is_confirmation_delete_open"
                  class="text-common-grey hover:text-red-500 ml-1 cursor-pointer w-5 h-5"
                  @click.prevent="
                    () => {
                      note_to_be_deleted = note
                      is_confirmation_delete_open = true
                    }
                  " />
              </div>
              <div class="flex flex-col items-end text-[12px] not-italic font-normal font-sans leading-normal text-common-grey">
                <span>{{ $utils.getUserById(note.created_by, "full_name") }}</span>
                <span>{{ toDate(note.last_edited) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="flex flex-col relative w-full h-[calc(100%-46px)] mt-1">
      <div v-if="recordId == 'this_page' && !is_edit_note" class="mb-1">
        <AsyncMultiSelectCompaniesInput
          add-label="Company/Opportunity"
          :clear-on-select="true"
          :is-box-background="true"
          classes="w-full min-w-[200px]"
          input-classes="appearance-none text-left block  px-3 py-2 border border-gray-300 rounded-md  placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm "
          name="companies"
          value-attr="id"
          :endpoint="`/api/organizations/${$store.state.organization.id}/records/filter/`"
          endpoint-method="post"
          search-field="name"
          type="text"
          :placeholder="$t('Company/Opportunity')"
          :multi-select="false"
          :show-icon="false"
          @update:currentItems="
            ($event) => {
              selected_record = $event
            }
          " />
      </div>
      <div class="mb-1">
        <MultiSelectInput
          v-model:current-items="note.categories"
          :value="note.categories"
          :disabled="false"
          name="Categories"
          :items="initial_categories"
          label-attr="name"
          value-attr="id"
          :allow-null="true"
          label="Type"
          classes=""
          input-classes="sm:text-sm appearance-none font-medium text-gray-700 text-left block  px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white focus:ring-1 focus:outline-none focus:ring-blue-light focus:border-blue-light dark:border-dark-divider-border dark:bg-dark-box-background"
          :placeholder="$t('Categories')"
          :show-label="false" />
      </div>
      <div class="mb-1">
        <TextInput
          :value="note.title"
          label=""
          :show-label="false"
          :classes="'w-full'"
          input-classes="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-light focus:border-blue-light sm:text-sm"
          name="name"
          type="text"
          :placeholder="$t('Title')"
          @update:modelValue="
            (e) => {
              note.title = e
            }
          " />
      </div>
      <div class="w-full relative rounded-md">
        <TextEditor
          :value="note.description"
          :show-label="false"
          :no-border="true"
          toolbar="essential"
          name="note"
          :label="$t('Note')"
          theme="snow"
          input-classes="w-full rounded-b-md"
          :placeholder="$t('Description')"
          @changed="
            (e) => {
              note.description = formatLink(e)
            }
          " />
      </div>
    </div>

    <div
      v-show="false"
      v-if="$route.name == 'CompanyProfile' || $route.name == 'ObjectProfile'"
      class="absolute bottom-1 right-2 text-xs text-common-grey cursor-pointer z-50 has-tooltip">
      <Info class="text-common-grey w-4 h-4" />
      <span
        class="tooltip -mt-10 text-xs -ml-28 whitespace-nowrap inline-flex items-center px-2 py-1 rounded-md bg-light-screen-background dark:bg-dark-screen-background font-medium dark:text-dark-body-text gap-2">
        {{ $t("showing on") }} {{ typeof label == "object" ? $t("this page") : $t("all pages") }}</span>
    </div>
    <!-- <div class="mt-3 mbn-2" v-html="note.description" /> -->

    <!-- Delete Note -->
    <Modal
      v-model:is-open="is_confirmation_delete_open"
      max-width="max-w-[600px] mx-5"
      :use-toast="true"
      default-success-msg="Note deleted successfully">
      <template #form="{ close: closeModal, done: showIsDone }">
        <h3 class="text-lg font-medium leading-6 text-gray-900 dark:text-dark-body-text">
          {{ $t("Delete Note") }}
        </h3>
        <div class="max-w-xl mt-2 text-sm text-gray-500">
          <p>{{ $t("Once you delete note, you can't restore it.") }}</p>
        </div>
        <div class="mt-5 text-center">
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 font-medium text-red-700 bg-red-100 border border-transparent rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:text-sm"
            @click="confirmDeleteNote(showIsDone)">
            {{ $t("Delete Note") }}
          </button>
          <button
            type="button"
            class="inline-flex items-center justify-center px-4 py-2 ml-2 font-medium border rounded-md dark:text-dark-body-text focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
            @click="closeModal">
            {{ $t("Cancel") }}
          </button>
        </div>
      </template>
    </Modal>
  </div>
</template>

<script>
import TextEditor from "@/components/shared/TextEditor.vue"
import dataService from "@/services/data.service"
import { FilePenLine, ChevronDown, Trash2, ChevronLeft, Info } from "lucide-vue-next"
import MultiSelectInput from "@/components/shared/MultiSelectInput.vue"
import TextInput from "@/components/shared/TextInput.vue"
import Modal from "@/components/shared/Modal.vue"
import AsyncMultiSelectCompaniesInput from "@/components/shared/AsyncMultiSelectCompaniesInput.vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

import Avatar from "@/components/shared/Avatar.vue"

import { Record } from "@/models/record"

export default {
  components: {
    Modal,
    Avatar,
    TextInput,
    FilePenLine,
    TextEditor,
    AsyncMultiSelectCompaniesInput,
    MultiSelectInput,
    Trash2,
    Info,
    ChevronDown,
    ChevronLeft
  },
  inject: ["canUserEditLayout"],
  props: {
    label: null,
    connectTo: null,
    linked_to: null,
    categories: {
      type: Array,
      default: () => []
    },
    recordId: null,
    notes: {
      type: Array,
      default: () => []
    },
    id: null
  },
  emits: [],

  data() {
    return {
      Record,
      tied_company: null,
      filter_is_open: false,
      fetched_categories: false,
      is_add_new_note: false,
      is_edit_note: false,
      is_confirmation_delete_open: false,
      note: { id: null, title: null, description: null, categories: [] },
      note_to_be_deleted: null,
      new_notes: [],
      fetched_all_notes: false,
      dont_trigger: false,
      loading: false,
      record_id: "this_page",
      selected_record: null,
      initial_categories: [],
      all_filters: [],
      selected_filters: []
    }
  },

  computed: {
    page_identifier() {
      if (this.connectTo == "this_page") {
        return `${this.$route.params.object_type}>${this.$route.params.cid}`
      } else {
        return "all"
      }
    }
  },

  watch: {
    recordId() {
      this.init()
    },
    fetched_all_notes() {
      if (this.fetched_all_notes) {
        let arr = this.new_notes.sort((objA, objB) => Number(new Date(objB.datetime_created)) - Number(new Date(objA.datetime_created)))
        this.new_notes = this.$utils.clone(arr)
      }
    },
    label() {
      this.init()
    },
    notes: {
      deep: true,
      handler() {
        if (!this.$utils.isEqual(this.$utils.clone(this.notes), this.$utils.clone(this.new_notes))) {
          this.init()
        }
      }
    }
  },

  created() {
    this.getFilteredCategories()
    this.init()
    this.register_listeners()
  },
  activated() {
    this.init()
    this.register_listeners()
  },
  deactivated() {
    this.unregister_listeners()
  },

  methods: {
    register_listeners() {
      this.$emitter.on(ALL_EMITTERS.NEW_NOTE_ADDED, ({ id: id, note: note }) => {
        if (this.record_id == id && !this.new_notes.find((n) => n.id == note.id)) {
          this.new_notes.unshift(note)
        }
      })
      this.$emitter.on(ALL_EMITTERS.NOTE_EDITED, ({ id: id, note: note }) => {
        if (this.record_id == id) {
          let index = this.new_notes.findIndex((n) => n.id == note.id)
          if (index != -1) {
            this.new_notes[index] = note
          }
        }
      })
      this.$emitter.on(ALL_EMITTERS.NOTE_DELETED, ({ id: id, note: note }) => {
        if (this.record_id == id) {
          let index = this.new_notes.findIndex((n) => n.id == note.id)
          if (index != -1) {
            this.new_notes.splice(index, 1)
          }
        }
      })
    },
    unregister_listeners() {
      this.$emitter.off(ALL_EMITTERS.NEW_NOTE_ADDED)
      this.$emitter.off(ALL_EMITTERS.NOTE_EDITED)
      this.$emitter.off(ALL_EMITTERS.NOTE_DELETED)
    },
    init() {
      if (this.recordId != "this_page") {
        if (this.linked_to == "this_record") {
          this.record_id = this.$route.params.cid
          this.tied_company = this.$store.state.ui.currentCompany
        } else {
          this.record_id = this.recordId
          this.$store.dispatch("data/getRecord", this.record_id).then((res) => {
            this.tied_company = res.data
          })
        }
        this.dont_trigger = true
        this.fetched_all_notes = false
        this.$store.dispatch("data/getRecordNotes", this.record_id).then((result) => {
          this.new_notes = []
          if (this.categories?.length == 10) {
            this.new_notes = this.$utils.clone(result.data.results)
          } else if (this.categories?.length > 0) {
            result.data.results.map((note) => {
              this.categories.map((cat) => {
                if (note.categories.includes(cat) && !this.new_notes.find((n) => n.id == note.id)) {
                  this.new_notes.push(note)
                }
              })
            })
          } else {
            this.new_notes = this.$utils.clone(result.data.results)
          }
          this.fetched_all_notes = true
          this.dont_trigger = false
        })
      } else if (this.linked_to == "all_records") {
        dataService.getNotes().then((result) => {
          this.new_notes = result.data.results
          this.fetched_all_notes = true
          this.dont_trigger = false
        })
      } else {
        this.dont_trigger = true
        const new_notes_ids = this.$utils.extractValue(this.$utils.clone(this.notes), "", this.$route)

        const notes_list_calc = []
        this.fetched_all_notes = false
        if (new_notes_ids && new_notes_ids.length) {
          new_notes_ids?.forEach((att_id, i) => {
            if (this.$store.state.portfolio.linked_notes.find((att) => att.id == att_id)) {
              notes_list_calc.push(this.$store.state.portfolio.linked_notes.find((att) => att.id == att_id))
              if (i == new_notes_ids?.length - 1) {
                setTimeout(() => {
                  this.fetched_all_notes = true
                  this.dont_trigger = false
                }, 1200)
              }
            } else {
              dataService.getNote(att_id).then((res) => {
                notes_list_calc.push(res.data)
                if (i == new_notes_ids?.length - 1) {
                  setTimeout(() => {
                    this.fetched_all_notes = true
                    this.dont_trigger = false
                  }, 1200)
                }
              })
            }
          })
          this.new_notes = notes_list_calc
        } else {
          this.fetched_all_notes = true
          setTimeout(() => {
            this.dont_trigger = false
          }, 400)
        }
      }
    },
    getFilteredCategories() {
      this.initial_categories = this.$store.state.data.note_categories
      this.fetched_categories = true
      if (this.categories && this.categories?.length > 0) {
        this.initial_categories = this.initial_categories.filter((cat) => this.categories.includes(cat.id))
        this.all_filters = this.initial_categories.filter((cat) => this.categories.includes(cat.id))
      }
      this.all_filters = this.$utils.clone(this.initial_categories)
    },
    addNewNote() {
      let index = this.new_notes.findIndex((note) => note.id == this.note?.id)
      if (index != -1) {
        //Update existing note
        if (this.recordId != "this_page") {
          dataService
            .updateRecordNote(this.record_id, this.note.id, {
              title: this.note.title,
              description: this.note.description,
              categories: this.note.categories
            })
            .then((res) => {
              this.new_notes[index] = res.data
              this.note = { title: null, description: null, categories: [] }
              this.$emitter.emit(ALL_EMITTERS.NOTE_EDITED, { id: this.record_id, note: res.data })
            })
        } else {
          if (this.selected_record && this.selected_record.length) {
            dataService
              .updateRecordNote(this.selected_record[0].id, this.note.id, {
                title: this.note.title,
                description: this.note.description,
                categories: this.note.categories
              })
              .then((res) => {
                this.new_notes[index] = res.data
                this.note = { title: null, description: null, categories: [] }
                this.$emitter.emit(ALL_EMITTERS.NOTE_EDITED, { id: this.selected_record[0].id, note: res.data })
              })
          } else {
            dataService
              .updateNote(this.note.id, { title: this.note.title, description: this.note.description, categories: this.note.categories })
              .then((res) => {
                this.new_notes[index] = res.data
                this.note = { title: null, description: null, categories: [] }
              })
          }
        }
      } else {
        //Create new note
        this.loading = true
        if (this.recordId != "this_page") {
          dataService
            .addNoteToRecord(this.record_id, {
              title: this.note.title,
              description: this.note.description,
              categories: this.note.categories
            })
            .then((result) => {
              this.new_notes.unshift(result.data)
              this.loading = false
              this.note = { title: null, description: null, categories: [] }
              this.$emitter.emit(ALL_EMITTERS.NEW_NOTE_ADDED, { id: this.record_id, note: result.data })
              this.$emitter.emit(ALL_EMITTERS.UPDATE_COUNT_NOTES_IN_RECORDS_TABLE, { id: this.record_id, count: this.new_notes.length })
            })
        } else {
          if (this.selected_record && this.selected_record.length) {
            dataService
              .addNoteToRecord(this.selected_record[0].id, {
                title: this.note.title,
                description: this.note.description,
                categories: this.note.categories
              })
              .then((result) => {
                this.new_notes.unshift(result.data)
                this.loading = false
                this.note = { title: null, description: null, categories: [] }
                this.$emitter.emit(ALL_EMITTERS.NEW_NOTE_ADDED, { id: this.selected_record[0].id, note: result.data })
                this.$emitter.emit(ALL_EMITTERS.UPDATE_NOTES_INSTANCE, {
                  label: this.label,
                  recordId: this.record_id,
                  categories: this.categories,
                  notes: this.new_notes.map((note) => note.id),
                  id: this.id,
                  component_type: "notes",
                  connectTo: this.connectTo,
                  page_identifier: this.page_identifier
                })
                this.$emitter.emit(ALL_EMITTERS.UPDATE_COUNT_NOTES_IN_RECORDS_TABLE, {
                  id: this.selected_record[0].id,
                  count: this.new_notes.length
                })
              })
          } else {
            dataService
              .addNote({ title: this.note.title, description: this.note.description, categories: this.note.categories })
              .then((result) => {
                this.new_notes.unshift(result.data)
                this.loading = false
                this.note = { title: null, description: null, categories: [] }
                this.$emitter.emit(ALL_EMITTERS.UPDATE_NOTES_INSTANCE, {
                  label: this.label,
                  recordId: this.record_id,
                  categories: this.categories,
                  notes: this.new_notes.map((note) => note.id),
                  id: this.id,
                  component_type: "notes",
                  connectTo: this.connectTo,
                  page_identifier: this.page_identifier
                })
                this.$emitter.emit(ALL_EMITTERS.UPDATE_COUNT_NOTES_IN_RECORDS_TABLE, { id: this.record_id, count: this.new_notes.length })
              })
          }
        }
      }
    },

    editNote(note) {
      this.is_edit_note = true
      this.note = note
    },

    confirmDeleteNote(showIsDone) {
      let index = this.new_notes.findIndex((note) => note.id == this.note_to_be_deleted.id)
      if (index != -1) {
        if (this.recordId != "this_page") {
          dataService.deleteRecordNote(this.record_id, this.note_to_be_deleted.id).then(() => {
            this.new_notes.splice(index, 1)
            this.$emitter.emit(ALL_EMITTERS.NOTE_DELETED, { id: this.record_id, note: this.note_to_be_deleted })
            this.$emitter.emit(ALL_EMITTERS.UPDATE_NOTES_INSTANCE, {
              label: this.label,
              notes: this.new_notes,
              recordId: this.record_id,
              categories: this.categories,
              id: this.id,
              component_type: "notes",
              connectTo: this.connectTo,
              page_identifier: this.page_identifier
            })
            showIsDone()
          })
        } else {
          if (this.selected_record && this.selected_record.length) {
            dataService.deleteRecordNote(this.selected_record[0].id, this.note_to_be_deleted.id).then(() => {
              this.new_notes.splice(index, 1)
              this.$emitter.emit(ALL_EMITTERS.NOTE_DELETED, { id: this.selected_record[0].id, note: this.note_to_be_deleted })
              this.$emitter.emit(ALL_EMITTERS.UPDATE_NOTES_INSTANCE, {
                label: this.label,
                notes: this.new_notes,
                recordId: this.record_id,
                categories: this.categories,
                id: this.id,
                component_type: "notes",
                connectTo: this.connectTo,
                page_identifier: this.page_identifier
              })
              showIsDone()
            })
          } else {
            dataService.deleteNote(this.note_to_be_deleted.id).then(() => {
              this.new_notes.splice(index, 1)
              this.$emitter.emit(ALL_EMITTERS.UPDATE_NOTES_INSTANCE, {
                label: this.label,
                notes: this.new_notes,
                recordId: this.recordId,
                categories: this.categories,
                id: this.id,
                component_type: "notes",
                connectTo: this.connectTo,
                page_identifier: this.page_identifier
              })
              showIsDone()
            })
          }
        }
      }
    },

    createUpdateNote() {
      if (this.is_add_new_note && !this.is_edit_note) {
        this.is_add_new_note = false
        this.addNewNote()
      } else if (!this.is_add_new_note && !this.is_edit_note) {
        this.is_add_new_note = true
      } else if (this.is_edit_note) {
        this.is_edit_note = false
        this.addNewNote()
      }
    },

    handleFilter(filter) {
      this.filter_is_open = !this.filter_is_open
      let index = this.selected_filters.findIndex((f) => f.id == filter.id)
      if (index != -1) {
        this.selected_filters.splice(index, 1)
      } else {
        this.selected_filters.push(filter)
      }
    },

    toDate(date_string) {
      let date = new Date(date_string)
      const options = { weekday: "long", year: "numeric", month: "long", day: "numeric" }
      let localStringDate = date.toLocaleDateString(undefined, options)
      return localStringDate.substr(localStringDate.indexOf(",") + 1)
    },

    formatLink(str) {
      return str.replace(/href="(?!http)/, 'href="http://')
    }
  }
}
</script>
